import { useEffect } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { Box, Stepper, Step, StepLabel, Typography } from "@mui/material";

import { useCurrentUser } from "@/auth/RequireAuth";
import { OnboardingStateProvider } from "./useOnboardingState";

const STEPS = [
  { label: "About you", path: "context" },
  { label: "Workspace", path: "workspace" },
  { label: "Plan", path: "plan" },
  { label: "Tools", path: "tools" },
] as const;

function stepIndex(pathname: string): number {
  const segment = pathname.split("/").pop() ?? "";
  const idx = STEPS.findIndex((s) => s.path === segment);
  return idx >= 0 ? idx : 0;
}

/** Server-authoritative step → path mapping. */
function serverStepToPath(step: string | undefined): string {
  switch (step) {
    case "workspace":
      return "workspace";
    case "plan":
      return "plan";
    case "tools":
      return "tools";
    case "done":
      return ""; // will redirect to /workspace
    default:
      return "context"; // pre-company default
  }
}

/** Onboarding wizard shell: progress indicator + Outlet for nested steps.
 *  Reads onboarding_step from /auth/me to enforce server-authoritative navigation. */
export function OnboardingLayout() {
  const user = useCurrentUser();
  const navigate = useNavigate();
  const location = useLocation();

  const currentStep = stepIndex(location.pathname);
  const serverStep = user?.onboarding_step;

  useEffect(() => {
    if (!user) return;

    // If onboarding is done, go to workspace
    if (serverStep === "done") {
      window.location.assign("/workspace");
      return;
    }

    // Guard against skipping ahead: if the user manually navigates to a step
    // beyond their server-reported progress, redirect back.
    const allowedPath = serverStepToPath(serverStep);
    const currentPath = location.pathname.split("/").pop() ?? "";

    // Context step is always reachable (client-owned pre-company)
    if (currentPath === "context") return;

    const allowedIdx = STEPS.findIndex((s) => s.path === allowedPath);
    if (allowedIdx >= 0 && currentStep > allowedIdx) {
      navigate(`/onboard/${allowedPath}`, { replace: true });
    }
  }, [user, serverStep, currentStep, location.pathname, navigate]);

  return (
    <OnboardingStateProvider>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          minHeight: "100vh",
          p: 3,
          pt: 6,
        }}
      >
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 4 }}>
          Welcome to Artie
        </Typography>

        <Stepper activeStep={currentStep} sx={{ width: "100%", maxWidth: 600, mb: 5 }}>
          {STEPS.map((step) => (
            <Step key={step.path}>
              <StepLabel>{step.label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <Box sx={{ width: "100%", maxWidth: 520 }}>
          <Outlet />
        </Box>
      </Box>
    </OnboardingStateProvider>
  );
}
